import React from 'react'
import { firstValueFrom, Observable } from 'rxjs'
import { AjaxResponse } from 'rxjs/ajax'
import {
  disableAccountingDemoMode,
  enableAccountingDemoMode,
  getAccountingSetupStatus,
  installFinanceDemoData,
  resetFinanceDemoData,
  startAccountingLiveUsage,
} from 'utils/api-routes-accounts'
import { useFinanceObservable } from './useFinanceObservable'
import {
  AccountingSetupChecklist,
  AccountingSetupStatus,
  FinanceApiResponse,
  FinanceDemoModeStatus,
  FinanceErrorPayload,
} from '../types/finance'

type DemoPayload = { profile?: string; tag?: string; force_new_batch?: boolean }
type ConfirmPayload = { confirmed: boolean; force?: boolean }

interface FinanceDemoModeContextValue {
  setupStatus: AccountingSetupStatus | null
  demoMode: FinanceDemoModeStatus | null
  checklist: AccountingSetupChecklist | null
  isLoading: boolean
  error: FinanceErrorPayload | string | null
  refresh: () => void
  bannerDismissed: boolean
  dismissBanner: () => void
  enableDemoMode: (_payload?: {
    reason?: string
  }) => Promise<FinanceApiResponse<Record<string, never>>>
  installDemoData: (
    _payload: DemoPayload,
  ) => Promise<FinanceApiResponse<Record<string, never>>>
  resetDemoData: (
    _payload: ConfirmPayload,
  ) => Promise<FinanceApiResponse<Record<string, never>>>
  startLiveUsage: (
    _payload: ConfirmPayload,
  ) => Promise<FinanceApiResponse<Record<string, never>>>
  disableDemoMode: (
    _payload: ConfirmPayload,
  ) => Promise<FinanceApiResponse<Record<string, never>>>
}

const FinanceDemoModeContext =
  React.createContext<FinanceDemoModeContextValue | null>(null)

const getTenantId = () => {
  try {
    const tenantInfo = JSON.parse(sessionStorage.getItem('tenant_info') || '{}')
    return tenantInfo?.tenant_id || 'default'
  } catch {
    return 'default'
  }
}

const getFingerprint = (demoMode: FinanceDemoModeStatus | null) => {
  if (!demoMode?.is_demo_mode) {
    return ''
  }

  return [
    demoMode.is_demo_mode,
    demoMode.demo_data_status,
    demoMode.last_installed_at || 'none',
    demoMode.last_reset_at || 'none',
  ].join(':')
}

const getDismissStorageKey = (tenantId: string, fingerprint: string) =>
  `financeDemoBannerDismissed:${tenantId}:${fingerprint}`

export const FinanceDemoModeProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const isTestEnv =
    typeof process !== 'undefined' && process.env?.NODE_ENV === 'test'
  const setup = useFinanceObservable<AccountingSetupStatus>(
    () =>
      getAccountingSetupStatus() as any as Observable<
        AjaxResponse<FinanceApiResponse<AccountingSetupStatus>>
      >,
    {
      immediate: !isTestEnv,
    },
  )
  const [bannerDismissed, setBannerDismissed] = React.useState(false)

  const demoMode = React.useMemo<FinanceDemoModeStatus | null>(() => {
    const source = setup.data?.demo_mode
    if (!source) {
      return null
    }

    return {
      is_demo_mode: !!source.is_demo_mode,
      demo_data_status: source.demo_data_status,
      cleanup_required_before_live_usage:
        !!source.cleanup_required_before_live_usage,
      last_installed_at: source.last_demo_batch?.created_at || null,
      last_reset_at: source.last_demo_reset_at || null,
      live_usage_started_at: source.live_usage_started_at || null,
      last_installed_batch: source.last_demo_batch || null,
      active_batch_count: source.demo_data_installed ? 1 : 0,
    }
  }, [setup.data])

  const tenantId = React.useMemo(() => getTenantId(), [])
  const fingerprint = React.useMemo(() => getFingerprint(demoMode), [demoMode])

  React.useEffect(() => {
    if (!fingerprint) {
      setBannerDismissed(false)
      return
    }

    try {
      setBannerDismissed(
        sessionStorage.getItem(getDismissStorageKey(tenantId, fingerprint)) ===
          '1',
      )
    } catch {
      setBannerDismissed(false)
    }
  }, [fingerprint, tenantId])

  const dismissBanner = React.useCallback(() => {
    if (!fingerprint) {
      return
    }
    try {
      sessionStorage.setItem(getDismissStorageKey(tenantId, fingerprint), '1')
    } catch {
      // ignore storage failures
    }
    setBannerDismissed(true)
  }, [fingerprint, tenantId])

  const refresh = React.useCallback(() => {
    setup.refresh()
  }, [setup])

  const performAction = React.useCallback(
    async <T,>(
      factory: () => Observable<AjaxResponse<FinanceApiResponse<T>>>,
    ): Promise<FinanceApiResponse<T>> => {
      const response = await firstValueFrom(factory())
      const payload = response?.response || ({} as FinanceApiResponse<T>)
      setup.refresh()
      return payload
    },
    [setup],
  )

  const value = React.useMemo<FinanceDemoModeContextValue>(
    () => ({
      setupStatus: setup.data || null,
      demoMode,
      checklist: setup.data?.setup_checklist || null,
      isLoading: setup.loading,
      error: (setup.error as any) || null,
      refresh,
      bannerDismissed,
      dismissBanner,
      enableDemoMode: (payload) =>
        performAction(
          () =>
            enableAccountingDemoMode(payload || {}) as any as Observable<
              AjaxResponse<FinanceApiResponse<Record<string, never>>>
            >,
        ),
      installDemoData: (payload) =>
        performAction(
          () =>
            installFinanceDemoData(payload) as any as Observable<
              AjaxResponse<FinanceApiResponse<Record<string, never>>>
            >,
        ),
      resetDemoData: (payload) =>
        performAction(
          () =>
            resetFinanceDemoData(payload) as any as Observable<
              AjaxResponse<FinanceApiResponse<Record<string, never>>>
            >,
        ),
      startLiveUsage: (payload) =>
        performAction(
          () =>
            startAccountingLiveUsage(payload) as any as Observable<
              AjaxResponse<FinanceApiResponse<Record<string, never>>>
            >,
        ),
      disableDemoMode: (payload) =>
        performAction(
          () =>
            disableAccountingDemoMode(payload) as any as Observable<
              AjaxResponse<FinanceApiResponse<Record<string, never>>>
            >,
        ),
    }),
    [
      setup.data,
      setup.loading,
      setup.error,
      demoMode,
      refresh,
      bannerDismissed,
      dismissBanner,
      performAction,
    ],
  )

  return (
    <FinanceDemoModeContext.Provider value={value}>
      {children}
    </FinanceDemoModeContext.Provider>
  )
}

export const useFinanceDemoMode = () => {
  const context = React.useContext(FinanceDemoModeContext)
  if (!context) {
    throw new Error(
      'useFinanceDemoMode must be used within FinanceDemoModeProvider',
    )
  }

  return context
}
